import { useEffect, useState, type CSSProperties } from 'react'
import { Modal } from 'react-native'
import { useTranslation } from 'react-i18next'

import { getJbpPaymentMethodLabel, type JbpPaymentMethodKind } from '@/utils/jbp'

interface JbpCheckoutProps {
  checkoutUrl: string
  method: JbpPaymentMethodKind
  onChooseOther: () => void
  onClose: () => void
}

export default function JbpCheckout({ checkoutUrl, method, onChooseOther, onClose }: JbpCheckoutProps) {
  const { t } = useTranslation()
  const paymentMethod = getJbpPaymentMethodLabel(method)
  const title = t('fastPay:checkout', { method: paymentMethod })
  const embeddedCheckout = /^https:\/\//i.test(checkoutUrl)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    setLoading(embeddedCheckout)
    setFailed(false)
  }, [checkoutUrl, reloadKey, embeddedCheckout])

  useEffect(() => {
    // Cross-origin iframe failures do not reliably raise onError in browsers.
    if (!embeddedCheckout || !loading) return
    const timeout = setTimeout(() => {
      setLoading(false)
      setFailed(true)
    }, 30000)
    return () => clearTimeout(timeout)
  }, [loading, checkoutUrl, reloadKey, embeddedCheckout])

  if (typeof document === 'undefined') return null

  return (
    <Modal onRequestClose={onClose} transparent visible>
      <div style={styles.overlay}>
        <section
          aria-label={title}
          aria-modal={'true'}
          role={'dialog'}
          style={embeddedCheckout ? styles.dialog : { ...styles.dialog, height: 'auto' }}
        >
          <header style={styles.header}>
            <strong style={styles.title}>{title}</strong>
            <button onClick={onClose} style={styles.close} type={'button'}>
              {t('fastPay:close')}
            </button>
          </header>
          {embeddedCheckout ? (
            <div style={styles.content}>
              <iframe
                allow={'payment; clipboard-write'}
                key={reloadKey}
                onError={() => {
                  setLoading(false)
                  setFailed(true)
                }}
                onLoad={() => setLoading(false)}
                src={checkoutUrl}
                style={styles.iframe}
                title={title}
              />
              {loading && !failed ? <div style={styles.loading}>{t('fastPay:checkoutLoading')}</div> : null}
              {failed ? (
                <div style={styles.loading}>
                  <p style={styles.message}>{t('fastPay:checkoutLoadFailed')}</p>
                  <button
                    onClick={() => {
                      setFailed(false)
                      setLoading(true)
                      setReloadKey((value) => value + 1)
                    }}
                    style={styles.retry}
                    type={'button'}
                  >
                    {t('fastPay:retry')}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div style={styles.externalContent}>
              <p style={styles.message}>{t('fastPay:externalCheckoutHint', { method: paymentMethod })}</p>
              {/* JBP can return HTTP URLs, which cannot be embedded in our HTTPS page. */}
              <a href={checkoutUrl} rel={'noopener noreferrer'} style={styles.openCheckout} target={'_blank'}>
                {t('fastPay:openCheckout')}
              </a>
              <p style={styles.hint}>{t('fastPay:externalCheckoutUnavailableHint')}</p>
            </div>
          )}
          {embeddedCheckout ? <p style={styles.hint}>{t('fastPay:checkoutHint', { method: paymentMethod })}</p> : null}
          <p style={styles.hint}>{t('fastPay:chooseOtherMethodHint')}</p>
          <button onClick={onChooseOther} style={styles.alternative} type={'button'}>
            {t('fastPay:chooseOtherMethod')}
          </button>
        </section>
      </div>
    </Modal>
  )
}

const styles: Record<string, CSSProperties> = {
  overlay: {
    alignItems: 'center',
    background: 'rgba(0, 0, 0, 0.35)',
    display: 'flex',
    inset: 0,
    justifyContent: 'center',
    padding: 16,
    position: 'fixed',
    zIndex: 1000
  },
  dialog: {
    background: '#FFFFFF',
    borderRadius: 12,
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.24)',
    display: 'flex',
    flexDirection: 'column',
    height: 'min(760px, calc(100vh - 32px))',
    maxWidth: 680,
    overflow: 'hidden',
    width: '100%'
  },
  header: {
    alignItems: 'center',
    borderBottom: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 56,
    padding: '0 20px'
  },
  title: {
    color: '#1F2937',
    fontSize: 18
  },
  close: {
    background: 'transparent',
    border: 0,
    color: '#3477DC',
    cursor: 'pointer',
    fontSize: 16,
    fontWeight: 600
  },
  content: {
    flex: 1,
    position: 'relative'
  },
  externalContent: {
    alignItems: 'center',
    color: '#4B5563',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: '32px 20px 0',
    textAlign: 'center'
  },
  openCheckout: {
    background: '#3477DC',
    borderRadius: 8,
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: 600,
    padding: '12px 28px',
    textDecoration: 'none'
  },
  iframe: {
    border: 0,
    display: 'block',
    height: '100%',
    width: '100%'
  },
  loading: {
    alignItems: 'center',
    background: '#FFFFFF',
    color: '#4B5563',
    display: 'flex',
    flexDirection: 'column',
    inset: 0,
    justifyContent: 'center',
    position: 'absolute'
  },
  message: {
    margin: 0
  },
  alternative: {
    background: '#FFFFFF',
    border: '1px solid #3477DC',
    borderRadius: 8,
    color: '#3477DC',
    cursor: 'pointer',
    fontSize: 15,
    padding: '12px 16px',
    margin: '0 20px 16px'
  },
  retry: {
    background: '#3477DC',
    border: 0,
    borderRadius: 8,
    color: '#FFFFFF',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 600,
    marginTop: 16,
    padding: '12px 28px'
  },
  hint: {
    color: '#6B7280',
    fontSize: 13,
    margin: 0,
    padding: '14px 20px',
    textAlign: 'center'
  }
}
